import { Controller } from "@hotwired/stimulus"
import { morphdomIgnore } from "web-ui/morphdom/ignore"
import { getCSRFToken } from "web-ui/csrf"
import { setInputValueAttributes } from "web-ui/form/set-input-value-attributes"
import * as Log from "web-ui/log"

// Set dynamic_form_debug_value to true to observe and diagnose

function isMetaInputName(name) {
  return name.startsWith("_")
}

function isAuthenticityToken(name) {
  return name == "authenticity_token"
}

// Connects to data-controller="dynamic-form"
export default class extends Controller {
  static values = {
    updateMode: { type: String, default: "form" }
  }

  connect() {
    this.debug("Connect", { updateMode: this.updateModeValue })

    this.connected = true

    this.changedElementNames = new Set()
    this.actionFormUpdates = {}
    this.updateParams = null
    this.submitting = false
    this.pendingSubmitter = null
    this.updateDelay = 0
    this.changed = false

    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)

    this.element.addEventListener("change", this.change)
    this.element.addEventListener("submit", this.submit)
    this.element.addEventListener("click", this.click)
  }

  disconnect() {
    this.debug("Disconnect")

    this.connected = false

    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)

    this.element.removeEventListener("change", this.change)
    this.element.removeEventListener("submit", this.submit)
    this.element.removeEventListener("click", this.click)

    if (this.fetchAbortController) {
      this.fetchAbortController.abort()
    }

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
    }
  }

  submit = (event) => {
    const submitter = event.submitter

    if (submitter == null) {
      return
    }

    this.debug("Submit")

    // Most forms only have a single submit button so it is unlikely that a
    // second submission will be attempted while another is pending. If it does
    // happen, the second submission will be disregarded and will have to be
    // retried after the first submission. - Aaron, Thu Oct 23 2025
    if (this.pendingSubmitter != null) {
      this.debug("Ignoring submission while another submission is pending")

      event.preventDefault()
      return
    }

    const updating = this.isUpdating()
    const hasPendingUpdates = this.hasPendingUpdates()
    const busy = updating || hasPendingUpdates

    if (busy) {
      this.debug("Suspending submission")

      event.preventDefault()

      submitter.setAttribute("disabled", "")
      morphdomIgnore(submitter, true)
      this.pendingSubmitter = submitter

      return
    }

    this.submitting = true
  }

  click = (event) => {
    const button = event.target.closest("button")

    if (button == null) {
      return
    }

    const dataset = button.dataset
    const actionFormUpdatesJSON = dataset.formUpdates

    if (actionFormUpdatesJSON == null) {
      return
    }

    event.preventDefault()
    this.applyActionFormUpdates(actionFormUpdatesJSON)
  }

  applyActionFormUpdates(actionFormUpdatesJSON) {
    this.changed = true

    const actionFormUpdates = JSON.parse(actionFormUpdatesJSON)
    for (const [name, value] of Object.entries(actionFormUpdates)) {
      this.actionFormUpdates[name] = value
    }

    this.requestUpdate()
  }

  change = (event) => {
    const input = event.target

    // Avoid updating disabled inputs, which can happen if a modification is
    // made to an input, and while it is still focused, an update disables that
    // input before it is blurred - Aaron, Wed Aug 09 2023
    if (input.disabled) {
      return
    }

    this.changed = true

    setInputValueAttributes(input)

    const name = input.name

    this.initiateChange(name)
  }

  initiateChange(name) {
    this.morphIgnoreInputs(name, true)
    this.changedElementNames.add(name)

    this.debug("Recorded change", { changedElementCount: this.changedElementNames.size, name: name })

    this.requestUpdate()
  }

  requestUpdate() {
    if (this.updateDelay > 0) {
      this.debug("Delaying update", { delayMilliseconds: this.updateDelay })
    }

    // Ensure update happens on the next tick, rather than the current, so that
    // an event handler may make additional changes to the form before the
    // update is submitted.
    // - Aaron, Shaun, Fri Jan 5 2023
    this.updateTimeout = setTimeout(() => {
      this.update()
    }, this.updateDelay)
  }

  dispatch(eventName) {
    const event = new CustomEvent(eventName, { bubbles: true })
    if (this.element.isConnected) {
      this.element.dispatchEvent(event)
    }

    return event
  }

  morphIgnoreInputs(name, ignore) {
    const formElements = this.element.elements

    for (const elementName in formElements) {
      if (elementName === name) {
        const element = formElements[elementName]

        if (element.forEach) {
          element.forEach(element => {
            morphdomIgnore(element, ignore)
          })
        } else {
          morphdomIgnore(element, ignore)
        }
      }
    }
  }

  async update() {
    if (this.isUpdating()) {
      return
    }

    if (this.isSubmitting()) {
      return
    }

    if (!this.hasPendingUpdates()) {
      return
    }

    this.dispatch("dynamic-form:before-update")

    const url = this.element.action

    const formData = new FormData(this.element)

    this.updateParams = new URLSearchParams()

    for (const [name, value] of formData) {
      if (this.includeUpdateParam(name)) {
        this.updateParams.append(name, value)
      }
    }

    this.changedElementNames.clear()

    const actionFormUpdates = this.actionFormUpdates
    this.actionFormUpdates = {}

    for (const [name, value] of Object.entries(actionFormUpdates)) {
      this.updateParams.append(name, value)
    }

    this.debug("Initiating update", { changedElementCount: this.changedElementNames.size, updateParams: Array.from(this.updateParams.keys()) })
    const csrfToken = getCSRFToken()

    this.fetchAbortController = new AbortController()
    const signal = this.fetchAbortController.signal

    const options = {
      method: "PATCH",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken
      },
      body: this.updateParams,
      signal
    }

    try {
      const response = await fetch(url, options)

      if (!response.ok) {
        if (!this.connected) {
          return
        }

        this.dispatch("dynamic-form:update-failed")
        this.retryUpdate()
        return
      }

      const html = await response.text()

      if (!this.connected) {
        return
      }

      Turbo.renderStreamMessage(html)

    } catch (error) {
      if (!this.connected) {
        return
      }

      Sentry.captureException(error)
      this.dispatch("dynamic-form:update-failed")

      this.retryUpdate()
    }
  }

  includeUpdateParam(name) {
    if (isAuthenticityToken(name)) {
      return false
    }

    if (isMetaInputName(name)) {
      return true
    }

    if (this.updateModeValue == "fields") {
      return this.changedElementNames.has(name)
    }

    return true
  }

  retryUpdate() {
    this.updateDelay += 250
    this.updateDelay = Math.min(this.updateDelay, 5000)

    for (const name of this.updateParams.keys()) {
      if (!isMetaInputName(name)) {
        this.changedElementNames.add(name)
      }
    }

    this.updateParams = null

    this.requestUpdate()
  }

  resetUpdateDelay() {
    this.updateDelay = 0
  }

  // The only known way to ensure that code can be run immediately before a
  // morph and immediately after it is to add an event listener for
  // turbo:before-stream-render and override the event.detail.render method.
  //
  // In the overridden render method:
  //
  // 1. All fields that have been updated have their morph ignore removed
  // 2. The original render is invoked, which applies the morph
  // 3. The update is marked as complete
  // 4. If there is a pending update, requestUpdate is invoked
  //
  // This code was originally added to prevent a race condition wherein a
  // field that was filled in could be subsequently cleared by a previous
  // update's morph being applied.
  // - Aaron, Thu Mar 21 2024
  beforeStreamRender = (event) => {
    const stream = event.detail.newStream
    const action = stream.getAttribute("action")
    const target = stream.getAttribute("target")

    if (action !== "morph") {
      return
    }

    if (target !== this.element.id) {
      return
    }

    const render = event.detail.render

    event.detail.render = (self) => {
      if (!this.isUpdating()) {
        render(self)

        if (this.submitting) {
          this.submitting = false

          if (this.hasPendingUpdates()) {
            this.requestUpdate()
          }
        }

        return
      }

      // This must be done here in case there is still a pending update when the
      // morphdom morph is applied for this update. If we did it earlier, a
      // twice updated field may flash its old value.
      // - Aaron, Wed Jul 20 2022
      for (const name of this.updateParams.keys()) {
        if (!this.changedElementNames.has(name)) {
          this.morphIgnoreInputs(name, false)
        }
      }

      this.updateParams = null

      render(self)

      this.debug("Completed update", { changedElementCount: this.changedElementNames.size })

      this.dispatch("dynamic-form:update")
      this.resetUpdateDelay()

      if (this.hasPendingUpdates()) {
        this.requestUpdate()
      } else if (this.pendingSubmitter) {
        this.debug("Resuming submission")

        const submitter = this.pendingSubmitter

        this.pendingSubmitter = null
        morphdomIgnore(submitter, false)
        this.element.requestSubmit(submitter)
      }
    }
  }

  hasPendingUpdates() {
    return this.changedElementNames.size > 0 ||
      Object.keys(this.actionFormUpdates).length > 0
  }

  isUpdating() {
    return this.updateParams != null
  }

  isSubmitting() {
    return this.submitting
  }

  hasUnsubmittedChanges() {
    if (this.hasPendingUpdates() || this.isUpdating()) {
      return true
    }

    if (this.updateModeValue != "fields") {
      return this.changed
    }

    return false
  }

  debug(message, data) {
    Log.debug("Dynamic Form - " + message, data)
  }
}
